// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import capitalize from 'lodash-es/capitalize'
import { useTranslation } from 'react-i18next'
import {
  ActionListItem,
  Avatar,
  Box,
  Button,
  Highlight,
  RowsSkeletonLoader,
  Tooltip,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { ListItemAnimation } from 'components/Animations/ListItemAnimation'
import { SkeletonListItemAnimation } from 'components/Animations/SkeletonListItemAnimation'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import {
  Contact,
  ContactEntity,
  ContactInstrument,
  ContactPaymentInstruments,
  RelationshipType,
} from 'types/contacts'

interface ContactsSearchListProps {
  contacts: Contact[] | ContactPaymentInstruments[]
  isFetchingNextPage: boolean
  isInstruments?: boolean
  hasLoadedMore: boolean
  hasNextPage: boolean
  handleContactSelect: (contact: Contact, contactInstrument?: ContactInstrument) => void
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<Contact[] | ContactPaymentInstruments[], Error>>
  setContactIds?: React.Dispatch<React.SetStateAction<string[]>>
  contactFilter?: string[]
}

export const ContactsSearchList = ({
  contacts,
  isFetchingNextPage,
  isInstruments = false,
  hasLoadedMore,
  hasNextPage,
  handleContactSelect,
  setHasLoadedMore,
  fetchNextPage,
  setContactIds,
  contactFilter,
}: ContactsSearchListProps) => {
  const { t } = useTranslation()

  const listRef = useRef(null)
  const [contactList, setContactList] = useState([])

  useEffect(() => {
    if (isFetchingNextPage && hasLoadedMore) {
      setContactList(prevItems => [...prevItems, { loading: true }, { loading: true }])
    } else if (contacts) {
      setContactList(contacts)
    }
  }, [contacts, isFetchingNextPage, hasLoadedMore])

  useEffect(() => {
    if (listRef?.current && hasLoadedMore) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [contactList, hasLoadedMore])

  const handleOnLoadMore = () => {
    fetchNextPage()
    setHasLoadedMore(true)
  }

  const handleOnShowAll = () => {
    setContactIds([])
  }

  const getHeaderRightHighLeftText = (contact: Contact) => {
    const isCompany = contact?.entity?.type === ContactEntity.COMPANY
    const isIndividual = contact?.entity?.type === ContactEntity.INDIVIDUAL
    const isSelfOwnedCompany =
      contact?.relationshipType === RelationshipType.SELF_OWNED && isCompany

    if (isSelfOwnedCompany) {
      return t('contacts.myCompany')
    }
    if (isCompany) {
      return t('contacts.company')
    }

    if (isIndividual) {
      return t('contacts.individual')
    }
  }

  return (
    <motion.ul ref={listRef} className="h-[calc(80vh-244px)] overflow-y-auto">
      <AnimatePresence>
        {contactList?.map((currentContact, index) => {
          const contact = isInstruments ? currentContact?.contact : currentContact
          const instrument = isInstruments && currentContact?.paymentInstrument
          const isLastTwo = contactList?.length - index <= 2
          const isNextToAnimate =
            contactList?.length - index > 2 && contactList?.length - index <= 10
          const headerHighlightLeftText = getHeaderRightHighLeftText(contact)

          if (currentContact?.loading) {
            return (
              <SkeletonListItemAnimation key={index} reference={`${contact?.reference}-${index}`}>
                <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
              </SkeletonListItemAnimation>
            )
          }

          return (
            <ListItemAnimation
              key={index}
              reference={contact?.reference}
              index={index}
              listLength={contactList?.length}
              hasLoadedMore={hasLoadedMore}
              isLastTwo={isLastTwo}
              isNextToAnimate={isNextToAnimate}
            >
              <ActionListItem
                key={contact?.id}
                id={contact?.id}
                title={contact?.name}
                testId={`contacts-step-list-item-${index}`}
                HeaderHighlightLeft={
                  <Highlight type="neutral" variant="emphasis">
                    {headerHighlightLeftText}
                  </Highlight>
                }
                Avatar={<Avatar type="entity" name={contact?.name ?? ''} />}
                BodyPre={
                  <p className="text-label min-w-[0px] line-clamp-1">{instrument?.description}</p>
                }
                onItemClick={() => handleContactSelect(contact, instrument ?? instrument)}
                {...(isInstruments && {
                  BodyAvatar: (
                    <Avatar
                      size="tiny"
                      type="currency"
                      name={instrument?.currency?.toLowerCase()}
                    />
                  ),
                })}
                {...(isInstruments && {
                  BodyPost: (
                    <Tooltip
                      bodyContent={`${instrument?.currency} (${capitalize(instrument?.network)}) - ${ellipseMiddleText(instrument?.address, 6, 6, '*')}`}
                      type="simple"
                      position="bottom"
                    >
                      <Box className="cursor-pointer">
                        <p className="text-body-regular whitespace-nowrap">{`${instrument?.currency}`}</p>
                      </Box>
                    </Tooltip>
                  ),
                })}
              />
            </ListItemAnimation>
          )
        })}

        <Box flex justify="center" className="mt-2">
          {!contactFilter?.length ? (
            <Button
              onClick={handleOnLoadMore}
              variant="ghost"
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {hasNextPage ? t('contacts.loadMore') : t('contacts.noMoreContacts')}
            </Button>
          ) : (
            <Button onClick={handleOnShowAll} variant="ghost">
              {t('contacts.showAll')}
            </Button>
          )}
        </Box>
      </AnimatePresence>
    </motion.ul>
  )
}
